// Pure evaluation of the publish-info notifications. Kept free of the redux
// store / api imports so it can be unit tested in isolation.
import { notificationTypes } from '../components/Default/NotificationCenter/constants'

const DAY_MS = 24 * 60 * 60 * 1000

// overridable per environment via runtime config (window._env_, see env.sh).
// read directly rather than via environment.js so this stays importable in tests.
const readDays = (key, fallback) => {
  const value = Number(window._env_?.[key])
  return Number.isFinite(value) && value >= 0 ? value : fallback
}

export const PUBLISH_DELAY_DAYS = readDays('PUBLISH_DELAY_DAYS', 7)
export const LOW_UPDATE_FREQUENCY_DAYS = readDays('LOW_UPDATE_FREQUENCY_DAYS', 90)

const toTime = (value) => {
  if (!value) {
    return null
  }
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * Decides which publish-info notification (if any) applies.
 *
 * - PUBLISH_DELAY: there are changes newer than the last publish (or the
 *   property was never published) and they're older than 7 days. Skipped while
 *   publishing isn't possible (see usePublishGuard), since the customer can't
 *   act on it.
 * - LOW_UPDATE_FREQUENCY: everything is published and the last update is older
 *   than 90 days.
 *
 * `dueAt` is the moment the condition started holding, used as the
 * notification's `createdAt` so the relative time reflects how long it's due.
 *
 * @param {{ publishedAt: ?string, updatedAt: ?string }} publishInfo
 * @param {number} [now] epoch milliseconds, injectable for testing
 * @param {{ canPublish?: boolean }} [options] canPublish from usePublishGuard
 * @returns {?{ type: string, dueAt: number }} a notificationTypes value, or null
 */
export const evaluatePublishInfo = (publishInfo, now = Date.now(), { canPublish = true } = {}) => {
  const updatedAt = toTime(publishInfo?.updatedAt)
  const publishedAt = toTime(publishInfo?.publishedAt)

  if (updatedAt === null) {
    return null
  }

  if (publishedAt === null || updatedAt > publishedAt) {
    if (!canPublish) {
      return null
    }
    const dueAt = updatedAt + PUBLISH_DELAY_DAYS * DAY_MS
    return dueAt < now ? { type: notificationTypes.PUBLISH_DELAY, dueAt } : null
  }

  const dueAt = updatedAt + LOW_UPDATE_FREQUENCY_DAYS * DAY_MS
  return dueAt < now ? { type: notificationTypes.LOW_UPDATE_FREQUENCY, dueAt } : null
}
